import React from 'react';
import { injectIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { ensureListing } from '../../util/data';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug } from '../../util/urlHelpers';
import { displayPrice } from '../../util/configHelpers';
import { resolveListingCondition } from '../../util/listingCondition';
import { NamedLink } from '../../components';
import css from './CompactListingCard.module.css';

const { Money } = sdkTypes;

// Simple customer commission calculation
const calculateCustomerPrice = (basePrice, config) => {
  if (!basePrice) return basePrice;

  let pct;
  if (config?.commission?.showCustomerCommission) {
    pct = config.commission.defaultCustomerCommissionPercentage;
  } else {
    return basePrice;
  }

  const amount = Math.round((basePrice.amount * pct) / 100);
  return new Money(basePrice.amount + amount, basePrice.currency);
};

// Bookmark / save icon SVG (from Figma)
const BookmarkIcon = ({ isFavorited }) => (
  <svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    {isFavorited ? (
      /* Filled bookmark */
      <path d="M7.33331 32.5827V7.67893C7.33331 6.83699 7.62498 6.12435 8.20831 5.54102C8.79165 4.95768 9.50429 4.66602 10.3462 4.66602H25.9871C26.829 4.66602 27.5416 4.95768 28.125 5.54102C28.7083 6.12435 29 6.83699 29 7.67893V32.5827L18.1666 27.9352L7.33331 32.5827Z" fill="var(--primary-olive, #4C541F)" />
    ) : (
      /* Outline bookmark */
      <path d="M7.33331 32.5827V7.67893C7.33331 6.83699 7.62498 6.12435 8.20831 5.54102C8.79165 4.95768 9.50429 4.66602 10.3462 4.66602H25.9871C26.829 4.66602 27.5416 4.95768 28.125 5.54102C28.7083 6.12435 29 6.83699 29 7.67893V32.5827L18.1666 27.9352L7.33331 32.5827ZM9.83331 28.7493L18.1666 25.166L26.5 28.7493V7.67893C26.5 7.5506 26.4465 7.4331 26.3396 7.32643C26.2329 7.21949 26.1154 7.16602 25.9871 7.16602H10.3462C10.2179 7.16602 10.1004 7.21949 9.99373 7.32643C9.88679 7.4331 9.83331 7.5506 9.83331 7.67893V28.7493Z" fill="var(--primary-olive, #4C541F)" />
    )}
  </svg>
);

// Store/pickup icon SVG (from Figma)
const StoreIcon = () => (
  <svg className={css.storeIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_store" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="40" height="40">
      <rect width="40" height="40" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_store)">
      <path d="M19.7499 10.7166V18.7048C19.7499 19.1651 19.5957 19.5495 19.2874 19.8578C18.9791 20.1661 18.5948 20.3203 18.1346 20.3203H4.36539C3.90506 20.3203 3.52072 20.1661 3.21239 19.8578C2.90406 19.5495 2.74989 19.1651 2.74989 18.7048V10.6781C2.32806 10.3666 2.01939 9.95823 1.82389 9.45306C1.62839 8.9479 1.62422 8.40881 1.81139 7.83581L2.78464 4.62806C2.91797 4.2204 3.13297 3.90048 3.42964 3.66831C3.72647 3.43631 4.09347 3.32031 4.53064 3.32031H17.9306C18.3678 3.32031 18.7338 3.42898 19.0286 3.64631C19.3236 3.86365 19.5397 4.17806 19.6769 4.58956L20.6884 7.83581C20.8756 8.40881 20.8714 8.95239 20.6759 9.46656C20.4804 9.98056 20.1717 10.3972 19.7499 10.7166ZM13.4499 10.3203C14.0922 10.3203 14.5717 10.1405 14.8884 9.78081C15.2051 9.42131 15.3384 9.0499 15.2884 8.66656L14.6229 4.32031H11.7499V8.52031C11.7499 9.01131 11.9178 9.43406 12.2536 9.78856C12.5896 10.1431 12.9884 10.3203 13.4499 10.3203ZM8.94989 10.3203C9.48706 10.3203 9.92139 10.1431 10.2529 9.78856C10.5842 9.43406 10.7499 9.01131 10.7499 8.52031V4.32031H7.87689L7.21139 8.74331C7.17039 9.05365 7.30597 9.39406 7.61814 9.76456C7.93031 10.1351 8.37422 10.3203 8.94989 10.3203ZM4.49989 10.3203C4.94089 10.3203 5.31556 10.1703 5.62389 9.87031C5.93222 9.57031 6.12422 9.19598 6.19989 8.74731L6.82689 4.32031H4.53064C4.31264 4.32031 4.13956 4.3684 4.01139 4.46456C3.88322 4.56073 3.78706 4.70498 3.72289 4.89731L2.79989 8.05481C2.63572 8.58048 2.70589 9.08948 3.01039 9.58181C3.31489 10.0741 3.81139 10.3203 4.49989 10.3203ZM17.9999 10.3203C18.5986 10.3203 19.0768 10.087 19.4346 9.62031C19.7923 9.15365 19.8807 8.63181 19.6999 8.05481L18.7269 4.85881C18.6627 4.66648 18.5666 4.52865 18.4384 4.44531C18.3102 4.36198 18.1371 4.32031 17.9191 4.32031H15.6729L16.2999 8.74731C16.3756 9.19598 16.5676 9.57031 16.8759 9.87031C17.1842 10.1703 17.5589 10.3203 17.9999 10.3203ZM4.36539 19.3203H18.1346C18.314 19.3203 18.4614 19.2626 18.5769 19.1473C18.6922 19.0318 18.7499 18.8843 18.7499 18.7048V11.1933C18.6152 11.2395 18.4873 11.2722 18.3661 11.2916C18.2451 11.3107 18.1231 11.3203 17.9999 11.3203C17.5499 11.3203 17.1541 11.2325 16.8124 11.0568C16.4707 10.8811 16.1461 10.6011 15.8384 10.2166C15.5769 10.5421 15.2545 10.8074 14.8711 11.0126C14.4878 11.2177 14.0217 11.3203 13.4729 11.3203C13.0742 11.3203 12.6951 11.2274 12.3354 11.0416C11.9759 10.8556 11.6141 10.5806 11.2499 10.2166C10.9152 10.5806 10.5444 10.8556 10.1374 11.0416C9.73039 11.2274 9.34222 11.3203 8.97289 11.3203C8.55372 11.3203 8.14514 11.2401 7.74714 11.0798C7.34897 10.9196 6.99989 10.6319 6.69989 10.2166C6.20889 10.7076 5.77972 11.0142 5.41239 11.1366C5.04506 11.2591 4.74089 11.3203 4.49989 11.3203C4.37689 11.3203 4.25289 11.3107 4.12789 11.2916C4.00289 11.2722 3.87689 11.2395 3.74989 11.1933V18.7048C3.74989 18.8843 3.80764 19.0318 3.92314 19.1473C4.03864 19.2626 4.18606 19.3203 4.36539 19.3203Z" fill="#1C1B1F" />
    </g>
  </svg>
);

// Shipping truck icon SVG (from Figma)
const ShippingIcon = () => (
  <svg className={css.shippingIcon} width="24" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.50738 16C3.6717 16 2.95981 15.7126 2.37171 15.1377C1.78361 14.5629 1.48956 13.8647 1.48956 13.0433H0V1.65709C0 1.1926 0.164342 0.800293 0.493027 0.480176C0.821906 0.160059 1.22471 0 1.70144 0H17.2789V4.15888H19.8238L24 9.61428V13.0433H22.2046C22.2046 13.8647 21.909 14.5629 21.318 15.1377C20.7272 15.7126 20.0098 16 19.1658 16C18.3256 16 17.6126 15.7126 17.0266 15.1377C16.4406 14.5629 16.1477 13.8647 16.1477 13.0433H7.54617C7.54617 13.8691 7.24872 14.5683 6.65383 15.1408C6.05874 15.7136 5.34326 16 4.50738 16ZM4.51786 15.0408C5.08753 15.0408 5.57057 14.8477 5.96697 14.4617C6.36317 14.0758 6.56128 13.6054 6.56128 13.0506C6.56128 12.4958 6.36317 12.0255 5.96697 11.6396C5.57057 11.2537 5.08753 11.0608 4.51786 11.0608C3.94819 11.0608 3.46526 11.2537 3.06905 11.6396C2.67284 12.0255 2.47474 12.4958 2.47474 13.0506C2.47474 13.6054 2.67284 14.0758 3.06905 14.4617C3.46526 14.8477 3.94819 15.0408 4.51786 15.0408ZM0.985181 12.084H1.64789C1.81359 11.5385 2.16458 11.0709 2.70088 10.6812C3.23718 10.2917 3.83983 10.097 4.50884 10.097C5.14816 10.097 5.74315 10.2888 6.29381 10.6724C6.84466 11.0562 7.20293 11.5268 7.36863 12.084H16.2941V0.9595H1.70144C1.52235 0.9595 1.3582 1.03216 1.20899 1.17748C1.05979 1.3228 0.985181 1.48267 0.985181 1.65709V12.084ZM19.176 15.0408C19.7456 15.0408 20.2287 14.8477 20.6251 14.4617C21.0213 14.0758 21.2194 13.6054 21.2194 13.0506C21.2194 12.4958 21.0213 12.0255 20.6251 11.6396C20.2287 11.2537 19.7456 11.0608 19.176 11.0608C18.6065 11.0608 18.1235 11.2537 17.7271 11.6396C17.3309 12.0255 17.1328 12.4958 17.1328 13.0506C17.1328 13.6054 17.3309 14.0758 17.7271 14.4617C18.1235 14.8477 18.6065 15.0408 19.176 15.0408ZM17.2789 9.76112H22.9103L19.2866 5.11809H17.2789V9.76112Z" fill="#1C1B1F" />
  </svg>
);

const CompactListingCard = props => {
  const { listing, intl, manufacturerImages, isIframe, onBookmarkClick, isFavorited } = props;
  const config = useConfiguration();

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const authorId = currentListing.author?.id?.uuid;
  const { title = '', price, publicData = {} } = currentListing.attributes || {};
  const slug = createSlug(title);

  const {
    brand,
    dealerName: listingDealerName,
    dealerLocation: listingDealerLocationRaw,
    pickupEnabled,
    shippingEnabled,
    location: listingLocation,
    isFromManufacturer,
    manufacturerListingId,
  } = publicData;

  // Author profile data for dealer name/location fallback
  const authorPublicData = currentListing.author?.attributes?.profile?.publicData || {};
  const dealerName = listingDealerName || authorPublicData.businessName || currentListing.author?.attributes?.profile?.displayName || '';
  const listingDealerLocation = listingDealerLocationRaw || authorPublicData.storedAddress?.place_name || '';

  // Handle manufacturer images
  let displayListing = currentListing;
  const hasImages = currentListing.images && currentListing.images.length > 0;
  if (isFromManufacturer && manufacturerListingId && !hasImages) {
    const originalImages = manufacturerImages?.[manufacturerListingId];
    if (originalImages && originalImages.length > 0) {
      displayListing = { ...currentListing, images: originalImages };
    }
  }

  const firstImage =
    displayListing.images && displayListing.images.length > 0 ? displayListing.images[0] : null;

  // Pick best image URL from available variants
  // Prefer cropped variants (fit=crop) over clipped ones (fit=clip) for consistent card display
  const imageVariants = firstImage?.attributes?.variants || {};
  const variantKeys = Object.keys(imageVariants);

  // 1. Try known cropped variants first
  // 2. Then try any variant with fit=crop in URL
  // 3. Fall back to any available variant
  const croppedVariant = variantKeys.find(k => {
    const url = imageVariants[k]?.url || '';
    return url.includes('fit=crop');
  });
  const imageUrl = imageVariants['listing-card']?.url
    || imageVariants['listing-card-2x']?.url
    || (croppedVariant ? imageVariants[croppedVariant].url : null)
    || imageVariants['scaled-medium']?.url
    || imageVariants['scaled-small']?.url
    || Object.values(imageVariants)[0]?.url
    || null;

  // Format as "City, State" from a full address string
  const formatCityState = address => {
    if (!address) return '';
    const parts = address.split(',').map(p => p.trim()).filter(Boolean);
    // Drop country names from the end
    const filtered = parts.filter(p => !/^(United States|USA|US|Canada|Mexico)$/i.test(p));
    const list = filtered.length > 0 ? filtered : parts;
    if (list.length >= 2) {
      const city = list[list.length - 2];
      const stateZip = list[list.length - 1].replace(/\s*\d{5}(-\d{4})?$/, '').trim();
      return stateZip ? `${city}, ${stateZip}` : city;
    }
    return list[0]?.replace(/\s*\d{5}(-\d{4})?$/, '').trim() || address;
  };

  const cityState = formatCityState(listingDealerLocation || listingLocation?.address || '');
  const locationText = [dealerName, cityState].filter(Boolean).join(' · ');

  // Price
  const adjustedPrice = calculateCustomerPrice(price, config);
  const formattedPrice =
    adjustedPrice && adjustedPrice.currency === config.currency
      ? formatMoney(intl, adjustedPrice)
      : null;

  // Listing type config for price display
  const listingType = publicData?.listingType;
  const foundListingTypeConfig = (config.listing.listingTypes || []).find(
    conf => conf.listingType === listingType
  );
  const showPrice = displayPrice(foundListingTypeConfig);

  const conditionLabel = resolveListingCondition(publicData);

  // Link props: iframe mode adds search param
  const linkToProps = isIframe ? { search: '?iframe=true' } : {};

  const handleSaveClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onBookmarkClick) {
      onBookmarkClick(id);
    }
  };

  return (
    <NamedLink
      className={css.card}
      name="ListingPage"
      params={{ id, slug }}
      to={linkToProps}
    >
      {/* Image area */}
      <div className={css.imageWrapper}>
        {conditionLabel && <div className={css.conditionBadge}>{conditionLabel}</div>}
        <div className={css.saveButton} onClick={handleSaveClick}>
          <BookmarkIcon isFavorited={isFavorited} />
        </div>
        {imageUrl ? (
          <img className={css.image} alt={title} src={imageUrl} />
        ) : (
          <div className={css.image} />
        )}
      </div>

      {/* Info section */}
      <div className={css.info}>
        <div className={css.title}>{title}</div>

        {brand && (
          <div className={css.brandRow}>
            <span className={css.byLabel}>By </span>
            {isIframe ? (
              <span className={css.storeName}>{brand}</span>
            ) : (
              <a
                className={css.storeName}
                href={`/s?pub_brand=${encodeURIComponent(brand)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
              >
                {brand}
              </a>
            )}
          </div>
        )}

        <div className={css.bottomRow}>
          {(dealerName || cityState) && (
            <div className={css.dealerLocation}>
              {dealerName && (
                authorId && !isIframe ? (
                  <a
                    className={css.dealerName}
                    href={`/store/${authorId}/detail`}
                    onClick={e => e.stopPropagation()}
                  >
                    {dealerName}
                  </a>
                ) : (
                  <div className={css.dealerName}>{dealerName}</div>
                )
              )}
              {cityState && <div className={css.dealerCity}>{cityState}</div>}
            </div>
          )}

          {(shippingEnabled || pickupEnabled) && (
            <div className={css.deliveryIcons}>
              {pickupEnabled && <StoreIcon />}
              {shippingEnabled && <ShippingIcon />}
            </div>
          )}

          {showPrice && formattedPrice && (
            <div className={css.price}>
              <span className={css.priceText}>{formattedPrice}</span>
            </div>
          )}
        </div>
      </div>
    </NamedLink>
  );
};

CompactListingCard.defaultProps = {
  manufacturerImages: {},
  isIframe: false,
  onBookmarkClick: null,
  isFavorited: false,
};

export default injectIntl(CompactListingCard);
